import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { getNamespaces, createApp } from '../services/api';

const COMPONENT_DEFAULTS = {
  backend: { port: 8000 },
  frontend: { port: 3000 },
};

const emptyComponentState = () => ({
  backend: { enabled: false, port: 8000, hostname: '', tlsSecretName: '' },
  frontend: { enabled: false, port: 3000, hostname: '', tlsSecretName: '' },
});

// Defined OUTSIDE AddAppModal — a component defined inside another
// component's function body gets recreated on every render, which makes
// React treat it as a new component type each time and remount its DOM
// (including any focused <input>), losing focus after every keystroke.
const ComponentFields = ({ name, label, componentState, onToggle, onFieldChange }) => {
  const c = componentState;
  return (
    <div className="border rounded p-3 space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={c.enabled} onChange={onToggle} />
        {label}
      </label>
      {c.enabled && (
        <div className="pl-6 space-y-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Port</label>
            <input
              type="number"
              value={c.port}
              onChange={(e) => onFieldChange('port', e.target.value)}
              className="w-32 border rounded px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hostname (for Ingress)</label>
            <input
              type="text"
              required={c.enabled}
              value={c.hostname}
              onChange={(e) => onFieldChange('hostname', e.target.value)}
              placeholder={`e.g. ${name}.example.com`}
              className="w-full border rounded px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">TLS secret name (optional)</label>
            <input
              type="text"
              value={c.tlsSecretName}
              onChange={(e) => onFieldChange('tlsSecretName', e.target.value)}
              placeholder="defaults to <namespace>-tls"
              className="w-full border rounded px-2 py-1 text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const AddAppModal = ({ onClose, onCreated }) => {
  const [namespaces, setNamespaces] = useState([]);
  const [loadingNamespaces, setLoadingNamespaces] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    appName: '',
    namespaceId: '',
    githubUsername: '',
    appInfo: '',
  });
  const [components, setComponents] = useState(emptyComponentState());

  useEffect(() => {
    getNamespaces()
      .then((res) => setNamespaces(res.data.namespaces || []))
      .catch(() => setError('Failed to load namespaces'))
      .finally(() => setLoadingNamespaces(false));
  }, []);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleComponent = (name) => (e) => {
    const enabled = e.target.checked;
    setComponents((c) => ({
      ...c,
      [name]: { ...c[name], enabled, port: c[name].port || COMPONENT_DEFAULTS[name].port },
    }));
  };

  const updateComponentField = (name, field, value) => {
    setComponents((c) => ({ ...c, [name]: { ...c[name], [field]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.namespaceId) {
      setError('Select a namespace.');
      return;
    }

    const selected = Object.entries(components).filter(([, c]) => c.enabled);
    if (selected.length === 0) {
      setError('Select at least one component (backend and/or frontend).');
      return;
    }
    for (const [name, c] of selected) {
      if (!c.hostname.trim()) {
        setError(`${name}: enter a hostname for its Ingress.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        components: selected.map(([name, c]) => ({
          name,
          port: Number(c.port),
          hostname: c.hostname.trim(),
          tlsSecretName: c.tlsSecretName.trim() || undefined,
        })),
      };
      const res = await createApp(payload);
      onCreated?.(res.data.app);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create app');
    } finally {
      setSubmitting(false);
    }
  };

  const ComponentFieldsFor = (name, label) => (
    <ComponentFields
      name={name}
      label={label}
      componentState={components[name]}
      onToggle={toggleComponent(name)}
      onFieldChange={(field, value) => updateComponentField(name, field, value)}
    />
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white text-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Add New App</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm rounded p-3">{error}</div>}

          <div>
            <label className="block text-sm font-medium mb-1">App name</label>
            <input
              type="text"
              required
              value={form.appName}
              onChange={update('appName')}
              placeholder="e.g. Sahal University"
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              This becomes the repo name — one repo holds every component below.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Namespace</label>
            {loadingNamespaces ? (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Loading namespaces…
              </div>
            ) : namespaces.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 rounded p-3">
                No namespaces are allocated to your company yet. Contact KaydSystems support to
                have one set up before creating an app.
              </div>
            ) : (
              <select
                required
                value={form.namespaceId}
                onChange={update('namespaceId')}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                <option value="">Select a namespace…</option>
                {namespaces.map((ns) => (
                  <option key={ns.id} value={ns.id}>
                    {ns.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Your GitHub username</label>
            <input
              type="text"
              required
              value={form.githubUsername}
              onChange={update('githubUsername')}
              placeholder="e.g. octocat"
              className="w-full border rounded px-3 py-2 text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              We'll create a private repo and grant this account admin access.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Components</label>
            <div className="space-y-2">
              {ComponentFieldsFor('backend', 'Backend')}
              {ComponentFieldsFor('frontend', 'Frontend')}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Pick at least one. Each gets its own folder, CI pipeline, and deploy manifest in
              the same repo.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">App info</label>
            <textarea
              value={form.appInfo}
              onChange={update('appInfo')}
              rows={3}
              placeholder="Language/framework, anything we should know"
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded border text-gray-700 border-gray-300 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || namespaces.length === 0}
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-50 flex items-center gap-2"
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              Create App
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAppModal;